import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CoreButton,
  CoreTooltip,
  CoreModal,
  CoreLoader,
} from "../components/komodo";
import { generateQuestions } from "../data/questionGenerator";
import { getString } from "../utils/localizationHelper";
import FullScreenLoader from "../components/FullScreenLoader";
import "./Quiz.scss";

// Quiz questions configuration based on original magellan-elisa-builder

const Quiz = () => {
  // Helper to determine if user can go back
  const canGoBack = () => currentQuestionIndex > 0;

  // Helper to determine if user can go forward
  const canGoForward = () => {
    // Must have answered current question (not skipped)
    const answer = answers[currentQuestion?.key];
    const isAnswered =
      answer &&
      answer.length > 0 &&
      !isAnswerSkipped(answer, currentQuestion?.key);
    // If we're on the last question (submit), ensure question number 6 is answered
    if (isLastQuestion()) {
      const q6 = questions.find((q) => q.number === 6);
      if (q6) {
        const q6Answer = answers[q6.key];
        const q6Answered =
          q6Answer && q6Answer.length > 0 && !isAnswerSkipped(q6Answer, q6.key);
        return !!q6Answered;
      }
      // if question 6 not present, fall back to requiring current answered
      return isAnswered;
    }

    return isAnswered;
  };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { answers, setAnswer, checkCompletion } =
    require("../context/QuizContext").useQuiz();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Generate questions dynamically based on current answers
  useEffect(() => {
    const generatedQuestions = generateQuestions(answers);
    setQuestions(generatedQuestions);
  }, [answers]);

  // Handle URL parameter for going to specific question
  useEffect(() => {
    const goToParam = searchParams.get("goTo");
    if (goToParam && questions.length > 0) {
      const questionIndex = questions.findIndex((q) => q.key === goToParam);
      if (questionIndex !== -1) {
        setCurrentQuestionIndex(questionIndex);
        // Clear the URL parameter after navigating
        setSearchParams({});
      }
    }
  }, [questions, searchParams, setSearchParams]);

  // Reset to first question when all answers are cleared (start over)
  useEffect(() => {
    const hasNoAnswers = Object.keys(answers).length === 0;
    if (hasNoAnswers && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(0);
    }
  }, [answers, currentQuestionIndex]);

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  // Helper function to check if an answer is a skip (repo-faithful)
  const isAnswerSkipped = (answer, questionKey) => {
    if (!answer || answer.length === 0) return false;
    const answerValue = answer[0];

    // Check if it's explicitly "skipped" or if it matches question's skip_value
    if (answerValue === "skipped") return true;

    // Check if this answer matches the question's skip_value
    const question = questions.find((q) => q.key === questionKey);
    if (
      question &&
      question.skip_value &&
      answerValue === question.skip_value
    ) {
      return true;
    }

    return false;
  };

  const isLastQuestion = () => currentQuestionIndex === totalQuestions - 1;

  // Show loading while questions are being generated
  if (!questions.length) {
    return (
      <FullScreenLoader>
        <CoreLoader
          variant="circular"
          functionality="indeterminate"
          size="small"
          information="false"
          alignment="center"
        />
        <h2>Loading questions...</h2>
      </FullScreenLoader>
    );
  }

  // Navigation handlers
  const goBack = () => {
    if (canGoBack()) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const goForward = () => {
    if (canGoForward()) {
      if (isLastQuestion()) {
        completeQuiz();
      } else {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }
  };

  // Helper function to process quiz results
  const processQuizResults = (answers) => {
    // Example logic: Customize this based on Angular's `quizHelper.runQuery` and `processQuizResults`
    const results = [];

    // Iterate through answers and generate result queries
    Object.keys(answers).forEach((questionKey) => {
      const answer = answers[questionKey];
      if (answer && answer.length > 0) {
        results.push({
          questionKey,
          answer: answer[0],
        });
      }
    });

    // Example: Group results or perform additional processing
    const groupedResults = results.reduce((acc, result) => {
      const group = result.questionKey.startsWith("q") ? "quiz" : "other";
      if (!acc[group]) acc[group] = [];
      acc[group].push(result);
      return acc;
    }, {});
    return groupedResults;
  };

  // Update the `completeQuiz` function to include result processing
  const completeQuiz = () => {
    // Process results before navigating to the results page
    const processedResults = processQuizResults(answers);
    console.log("Processed Results:", processedResults);

    // Store answers and results in localStorage
    localStorage.setItem("elisaQuizAnswers", JSON.stringify(answers));
    localStorage.setItem("elisaQuizResults", JSON.stringify(processedResults));

    // Call checkCompletion from context to set completion state
    if (checkCompletion) {
      checkCompletion();
    }

    navigate("/results");
  };

  // Answer selection handler
  const selectOption = (optionKey) => {
    setAnswer(currentQuestion.key, [optionKey]);
  };

  const handleOptionKeyDown = (event, optionKey, visibleOptions) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectOption(optionKey);
      return;
    }

    const currentIndex = visibleOptions.findIndex(
      (option) => option.key === optionKey,
    );
    let nextIndex;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % visibleOptions.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex =
          (currentIndex - 1 + visibleOptions.length) % visibleOptions.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = visibleOptions.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextOption = visibleOptions[nextIndex];
    selectOption(nextOption.key);
    event.currentTarget
      .closest('[role="radiogroup"]')
      ?.querySelector(`[data-option-key="${nextOption.key}"]`)
      ?.focus();
  };

  // Progress bar navigation
  const goToQuestion = (questionNumber) => {
    const targetIndex = questionNumber - 1;
    if (targetIndex >= 0 && targetIndex < totalQuestions) {
      setCurrentQuestionIndex(targetIndex);
    }
  };

  // Skip question handler (Angular-faithful)
  const skipQuestion = () => {
    if (currentQuestion.allow_skip) {
      setAnswer(currentQuestion.key, [currentQuestion.skip_value || "unsure"]);
      // Optionally: trackAnswer(currentQuestion); // if you have analytics
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Ensure all functions are defined and used properly
  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const getOptionsLayoutClass = () => {
    if (!currentQuestion) return "";

    const visibleOptions =
      currentQuestion.options?.filter((option) =>
        currentQuestion.isOptionVisible
          ? currentQuestion.isOptionVisible(option.key)
          : true,
      ) || [];

    const optionCount = visibleOptions.length;
    if (optionCount <= 2) return "tf-options-2";
    if (optionCount <= 3) return "tf-options-3";
    if (optionCount <= 4) return "tf-options-4";
    if (optionCount > 8) return "tf-options-mini";
    return `tf-options-${Math.min(optionCount, 6)}`;
  };

  // Helper to render a question block
  const renderQuestionBlock = (qIdx, qObj, extraClass = "") => {
    const blockSelectedOptions = answers[qObj.key] || [];
    const visibleOptions =
      qObj.options?.filter((option) =>
        qObj.isOptionVisible ? qObj.isOptionVisible(option.key) : true,
      ) || [];
    const focusableOptionKey = blockSelectedOptions[0] || visibleOptions[0]?.key;
    const questionId = `quiz-question-${qObj.key}`;
    return (
      <div className="tf-quiz-question-inner">
        {/* Question Text */}
        <div
          id={questionId}
          className={`tf-question-text ${qObj.allow_skip ? "skipable" : ""}`}
          dangerouslySetInnerHTML={{
            __html: qObj.text,
          }}
        ></div>

        {/* Skip Option for specific questions */}
        {qIdx === currentQuestionIndex && qObj.number === 3 && (
          <div className="question-skip">
            {getString("quiz.modal.link_title")}&nbsp;
            <button className="tf-link" type="button" onClick={openModal}>
              {getString("quiz.modal.link")}
            </button>
          </div>
        )}

        {/* General Skip Option for skippable questions, except last question */}
        {qObj.allow_skip &&
          qIdx === currentQuestionIndex &&
          !isLastQuestion() && (
            <div className="question-skip">
              {getString("quiz.unsure")}&nbsp;
              <button onClick={skipQuestion} className="tf-link" type="button">
                {getString("quiz.skip")}
              </button>
            </div>
          )}

        {/* DOM order intentionally matches keyboard order: Previous, options, Next. */}
        <div
          className={`tf-question-focus-flow ${qIdx === 0 ? "first" : ""}`}
        >
          {canGoBack() && qIdx === currentQuestionIndex && (
            <div className="tf-question-action tf-question-action-back">
              <CoreButton
                onClick={goBack}
                variant="information"
                size="large"
                type="button"
                className="tf-btn-back"
              >
                <span slot="content">{getString("quiz.previous")}</span>
              </CoreButton>
            </div>
          )}

          <div
            className={`tf-options ${getOptionsLayoutClass()}`}
            role="radiogroup"
            aria-labelledby={questionId}
          >
            {visibleOptions.map((option) => (
              <div
                key={option.key}
                className={`tf-option ${
                  blockSelectedOptions.includes(option.key)
                    ? "tf-option-selected"
                    : ""
                }`}
                onClick={() => selectOption(option.key)}
                onKeyDown={(event) =>
                  handleOptionKeyDown(event, option.key, visibleOptions)
                }
                role="radio"
                tabIndex={option.key === focusableOptionKey ? 0 : -1}
                aria-checked={blockSelectedOptions.includes(option.key)}
                data-option-key={option.key}
              >
                <div className="tf-option-content">
                  {option.icon && (
                    <i className={`tf-option-icon ${option.icon}`}></i>
                  )}
                  <span
                    dangerouslySetInnerHTML={{ __html: option.text }}
                  ></span>
                  {blockSelectedOptions.includes(option.key) && (
                    <i className="tf-komodo-24-icon-checkmark-bold"></i>
                  )}
                  {option.tooltip && (
                    <CoreTooltip
                      label=""
                      alignment="center"
                      placement="top"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <i
                        className="tf-ui-icon-info-circle tf-option-tooltip"
                        aria-hidden="true"
                      ></i>
                      <div
                        slot="tooltip-content"
                        className="tf-option-tooltip-content"
                      >
                        {option.tooltip}
                      </div>
                    </CoreTooltip>
                  )}
                </div>
                {option.description && (
                  <div className="tf-option-description">
                    {option.description}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="tf-question-action tf-question-action-next">
            <CoreButton
              onClick={isLastQuestion() ? completeQuiz : goForward}
              disabled={!canGoForward()}
              variant={canGoForward() ? "primary" : "disabled"}
              size="large"
              type="button"
              className="tf-btn-forward"
            >
              <span slot="content">
                {isLastQuestion()
                  ? getString("quiz.submit")
                  : getString("quiz.next")}
              </span>
            </CoreButton>
          </div>
        </div>
      </div>
    );
  };

  // Replace hardcoded strings with localized strings where applicable
  return (
    <div className={`tf-page tf-quiz-panel`}>
      {/* Progress Bar - Exact Angular Structure */}
      <div className="tf-container quiz-progress">
        <div className="tf-elisa-quiz-progress">
          <div className="tf-elisa-progress-bar">
            <ol className={`tf-${totalQuestions}`}>
              {questions.map((question, index) => {
                const answer = answers[question.key];
                const isCurrentQuestion = index === currentQuestionIndex;
                const isAnswered =
                  answer &&
                  answer.length > 0 &&
                  !isAnswerSkipped(answer, question.key);
                const isSkipped = isAnswerSkipped(answer, question.key);
                const isLast = index === totalQuestions - 1;

                return (
                  <li
                    key={question.key}
                    className={`
                      ${isCurrentQuestion ? "current" : ""}
                      ${isAnswered || isSkipped ? "done" : ""}
                      ${isLast ? "last" : ""}
                    `.trim()}
                  >
                    {/* Progress marker - Angular exact structure */}
                    {isAnswered ? (
                      <button
                        onClick={() => goToQuestion(question.number)}
                        className="tf-elisa-marker"
                        type="button"
                        aria-label={`Question ${question.number} completed - ${question.label}`}
                      >
                        <i className="tf-komodo-24-icon-checkmark-mono"></i>
                      </button>
                    ) : isSkipped ? (
                      <button
                        onClick={() => goToQuestion(question.number)}
                        className="tf-elisa-marker"
                        type="button"
                        aria-label={`Question ${question.number} skipped - ${question.label}`}
                      >
                        <i className="tf-komodo-24-icon-checkmark-mono"></i>
                      </button>
                    ) : (
                      <span
                        className="tf-elisa-marker"
                        aria-label={`Question ${question.number} - ${question.label}`}
                      >
                        {question.number}
                      </span>
                    )}

                    {/* Question label and edit link - Angular exact structure */}
                    <div className="tf-elisa-label">
                      <p>{question.label}</p>
                      {(isAnswered || isSkipped) && !isCurrentQuestion && (
                        <button
                          onClick={() => goToQuestion(question.number)}
                          className="tf-link"
                          type="button"
                          aria-label={`${getString("quiz.edit")} ${question.label}`}
                        >
                          {getString("quiz.edit")}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* Mobile progress indicator - Angular structure */}
            <div className="tf-visible-sm-only">
              {currentQuestion.number} of {totalQuestions}:{" "}
              <strong>{currentQuestion.label}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Main Quiz Container: slide animation using transform */}
      <div className="tf-quiz-outer">
        <div className="tf-quiz-container">
          <div className="tf-container">
            <div className="tf-quiz-questions">
              <div
                className="tf-quiz-slide-container"
                style={{
                  transform: `translateX(-${currentQuestionIndex * 100}%)`,
                }}
              >
                {questions.map((question, index) => {
                  const isActive = index === currentQuestionIndex;
                  return (
                    <div
                      key={question.key}
                      className="tf-quiz-question"
                      // aria-hidden removes inactive slides from the accessibility tree.
                      // inert prevents Tab focus and pointer events from reaching off-screen
                      // slides, which otherwise causes the browser to scroll them into view
                      // and break the carousel layout during keyboard navigation.
                      aria-hidden={!isActive ? true : undefined}
                      {...(!isActive ? { inert: "" } : {})}
                    >
                      {renderQuestionBlock(index, question)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ELISA Types Modal - Advanced Implementation */}
      {isModalOpen && (
        <CoreModal
          className="elisa-types-modal"
          isopen={isModalOpen}
          onModalClosed={closeModal}
          bgclose="true"
          closeicon="false"
          customcontent="true"
          width="720px"
          nooverflow="false"
        >
          <div slot="custom-content" className="tf-modal-content">
            <i
              className="tf-komodo-24-icon-close-mono tf-close"
              onClick={closeModal}
              aria-label="Close modal"
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") closeModal();
              }}
            ></i>
            <h2>{getString("quiz.modal.title")}</h2>
            <div className="tf-modal-body">
              <div className="row">
                <div className="tf-col-sm-4 tf-text-center tf-mb-20">
                  <img
                    className="tf-modal-img"
                    src="assets/direct_assay.png"
                    alt="Direct Assay"
                  />
                  <strong>{getString("quiz.modal.direct")}</strong>
                </div>
                <div className="tf-col-sm-4 tf-text-center tf-mb-20">
                  <img
                    className="tf-modal-img"
                    src="assets/indirect_assay.png"
                    alt="Indirect Assay"
                  />
                  <strong>{getString("quiz.modal.indirect")}</strong>
                </div>
                <div className="tf-col-sm-4 tf-text-center tf-mb-20">
                  <img
                    className="tf-modal-img"
                    src="assets/capture_assay_sandwich.png"
                    alt="Capture Assay Sandwich"
                  />
                  <strong>{getString("quiz.modal.capture")}</strong>
                </div>
              </div>
              <div className="tf-modal-description">
                <div
                  dangerouslySetInnerHTML={{
                    __html: getString("quiz.modal.body"),
                  }}
                />
              </div>
            </div>
          </div>
        </CoreModal>
      )}
    </div>
  );
};
export default Quiz;
